// Shared media-type detection for the Asset Library. The Asset Prisma model
// has no stored mimetype/media-type column (`kind` is an open-ended string,
// `imageKey`/`imageUrl` is the only media reference -- see schema.prisma's
// Asset model doc comment), so video-ness is inferred from the resolved
// URL's file extension instead of a real content-type check. That's
// sufficient here: every video asset is either freshly uploaded through
// server/routes/assets.js's ALLOWED_VIDEO_TYPES gate (S3 key keeps the
// original extension) or a full external URL a human/agent attached, which
// also always carries a real extension. No new Prisma column, no migration.
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v'];

export function isVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => clean.endsWith(ext));
}

// A pasted YouTube/Vimeo watch-page link (Asset Library's "Link" mode, see
// AddAssetPopover.jsx) has no file extension and isn't directly playable via
// a <video> tag -- it needs an <iframe> embed instead to actually stream
// inline. Returns the embed URL for a recognized host's watch-page link, or
// null for anything else (a direct video/image URL, handled by isVideoUrl()
// above instead, or an unrecognized link -- callers should fall back to
// treating those as a plain image/video src).
export function getEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');

  if (host === 'youtube.com') {
    const videoId = parsed.searchParams.get('v');
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    const shortsMatch = parsed.pathname.match(/^\/shorts\/([^/]+)/);
    return shortsMatch ? `https://www.youtube.com/embed/${shortsMatch[1]}` : null;
  }
  if (host === 'youtu.be') {
    const videoId = parsed.pathname.slice(1);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }
  if (host === 'vimeo.com') {
    const videoId = parsed.pathname.split('/').filter(Boolean)[0];
    return videoId && /^\d+$/.test(videoId) ? `https://player.vimeo.com/video/${videoId}` : null;
  }
  // A plain pasted Google Drive share link (e.g.
  // https://drive.google.com/file/d/<FILE_ID>/view?usp=sharing) -- separate
  // from this app's own OAuth Drive-picker flow (googleDriveClient.js's
  // getDriveStatus/openDrivePicker/downloadDriveFileAsFile, which actively
  // downloads a file the user picks via that flow's own "Drive" mode). This
  // is just a link a human pasted, same trust model as a YouTube link: it
  // only renders anything for a viewer the file is already shared with, no
  // new access-control surface. /preview is Drive's embeddable iframe form.
  if (host === 'drive.google.com') {
    const fileMatch = parsed.pathname.match(/^\/file\/d\/([^/]+)/);
    return fileMatch ? `https://drive.google.com/file/d/${fileMatch[1]}/preview` : null;
  }
  return null;
}
